import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const RegisterPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    accountType: '', // 'individual' veya 'corporate'
    phone: '',
    verificationCode: '',
    fullName: '',
    username: '',
    password: '',
    confirmPassword: ''
  });
  
  const [verificationCodeSent, setVerificationCodeSent] = useState(false);
  const [codeVerified, setCodeVerified] = useState(false);
  const [codeExpiresAt, setCodeExpiresAt] = useState(null);
  const [codeTimer, setCodeTimer] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [usernameValidation, setUsernameValidation] = useState({
    checking: false,
    available: null,
    message: ''
  });
  const [countryCode, setCountryCode] = useState('+90');
  const [membershipAgreementText, setMembershipAgreementText] = useState('');
  const [privacyPolicyText, setPrivacyPolicyText] = useState('');
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const cloudflareWidgetRef = useRef(null);
  const [cloudflareToken, setCloudflareToken] = useState(null);

  // Kod geri sayım timer'ı
  useEffect(() => {
    if (codeTimer > 0) {
      const timer = setTimeout(() => setCodeTimer(codeTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [codeTimer]);

  // Cloudflare Turnstile yükle
  useEffect(() => {
    if (currentStep === 2 && !cloudflareWidgetRef.current?.dataset?.widgetId) {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        if (window.turnstile && cloudflareWidgetRef.current) {
          const widgetId = window.turnstile.render(cloudflareWidgetRef.current, {
            sitekey: '0x4AAAAAAABkMYinukE1B1Dv', // Cloudflare site key (test için)
            callback: (token) => {
              setCloudflareToken(token);
            },
            'error-callback': () => {
              setCloudflareToken(null);
            },
            'expired-callback': () => {
              setCloudflareToken(null);
            }
          });
          if (cloudflareWidgetRef.current) {
            cloudflareWidgetRef.current.dataset.widgetId = widgetId;
          }
        }
      };
      document.body.appendChild(script);
      
      return () => {
        if (cloudflareWidgetRef.current?.dataset?.widgetId) {
          window.turnstile?.remove(cloudflareWidgetRef.current.dataset.widgetId);
        }
        const existingScript = document.querySelector('script[src*="turnstile"]');
        if (existingScript) {
          existingScript.remove();
        }
      };
    }
  }, [currentStep]);

  // Kullanıcı adı kontrolü
  const checkUsernameAvailability = async (username) => {
    if (!username || username.length < 3) {
      setUsernameValidation({
        checking: false,
        available: false,
        message: 'Kullanıcı adı en az 3 karakter olmalıdır'
      });
      return;
    }

    setUsernameValidation({
      checking: true,
      available: null,
      message: 'Kontrol ediliyor...'
    });

    try {
      const response = await fetch('/api/check-username', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username })
      });

      const data = await response.json();

      if (data.success) {
        setUsernameValidation({
          checking: false,
          available: data.available,
          message: data.available ? 'Kullanıcı adı müsait' : 'Bu kullanıcı adı zaten kullanılıyor'
        });
      }
    } catch (error) {
      setUsernameValidation({
        checking: false,
        available: false,
        message: 'Kontrol edilemedi'
      });
    }
  };

  // Debounced username check
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (formData.username && currentStep === 3) {
        checkUsernameAvailability(formData.username);
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.username, currentStep]);

  // Hizmet sözleşmesi ve gizlilik politikası metinlerini yükle
  useEffect(() => {
    const loadMembershipAgreement = async () => {
      if (currentStep === 4) {
        try {
          const response = await fetch('/api/admin/settings');
          const data = await response.json();
          if (data.success) {
            // Hizmet sözleşmesi
            if (data.data.membership_agreement_message) {
              let agreementText = data.data.membership_agreement_message;
              // Değişkenleri form verileriyle değiştir
              const basvuruTarihi = new Date().toLocaleDateString('tr-TR');
              // Telefon formatını düzenle (0 ile başlayan format)
              let formattedPhone = formData.phone || '{basvuru-telefon}';
              if (formattedPhone && !formattedPhone.startsWith('{')) {
                const cleanPhone = formattedPhone.replace(/\D/g, '');
                if (cleanPhone.startsWith('90') && cleanPhone.length >= 12) {
                  formattedPhone = '0' + cleanPhone.substring(2);
                }
              }
              agreementText = agreementText
                .replace(/{uye-adi}/g, formData.fullName || '{uye-adi}')
                .replace(/{kullanici-adi}/g, formData.username || '{kullanici-adi}')
                .replace(/{basvuru-tarihi}/g, basvuruTarihi)
                .replace(/{basvuru-telefon}/g, formattedPhone);
              setMembershipAgreementText(agreementText);
            } else {
              setMembershipAgreementText('Hizmet sözleşmesi yüklenemedi. Lütfen sayfayı yenileyin.');
            }
            
            // Gizlilik politikası
            if (data.data.privacy_policy_message) {
              setPrivacyPolicyText(data.data.privacy_policy_message);
            } else {
              setPrivacyPolicyText('Gizlilik politikası yüklenemedi. Lütfen sayfayı yenileyin.');
            }
          } else {
            setMembershipAgreementText('Hizmet sözleşmesi yüklenemedi. Lütfen sayfayı yenileyin.');
            setPrivacyPolicyText('Gizlilik politikası yüklenemedi. Lütfen sayfayı yenileyin.');
          }
        } catch (error) {
          console.error('Sözleşmeler yüklenemedi:', error);
          setMembershipAgreementText('Hizmet sözleşmesi yüklenemedi. Lütfen sayfayı yenileyin.');
          setPrivacyPolicyText('Gizlilik politikası yüklenemedi. Lütfen sayfayı yenileyin.');
        }
      }
    };
    
    loadMembershipAgreement();
  }, [currentStep, formData.fullName, formData.username, formData.phone]);

  // Telefon formatı
  const formatPhoneNumber = (value, country = '+90') => {
    const numbers = value.replace(/\D/g, '');
    
    // +90 seçildiyse 0 ile başlamasın
    if (country === '+90') {
      // Eğer 0 ile başlıyorsa kaldır
      let cleanNumbers = numbers.startsWith('0') ? numbers.slice(1) : numbers;
      
      if (cleanNumbers.length <= 3) {
        return cleanNumbers;
      } else if (cleanNumbers.length <= 6) {
        return `${cleanNumbers.slice(0, 3)} ${cleanNumbers.slice(3)}`;
      } else if (cleanNumbers.length <= 8) {
        return `${cleanNumbers.slice(0, 3)} ${cleanNumbers.slice(3, 6)} ${cleanNumbers.slice(6)}`;
      } else {
        return `${cleanNumbers.slice(0, 3)} ${cleanNumbers.slice(3, 6)} ${cleanNumbers.slice(6, 8)} ${cleanNumbers.slice(8, 10)}`;
      }
    } else {
      // Diğer ülkeler için eski format (0 ile başlayabilir)
      let cleanNumbers = numbers;
      if (numbers.length > 0 && !numbers.startsWith('0')) {
        cleanNumbers = '0' + numbers;
      }
      
      if (cleanNumbers.length <= 1) {
        return cleanNumbers;
      } else if (cleanNumbers.length <= 4) {
        return `0(${cleanNumbers.slice(1)})`;
      } else if (cleanNumbers.length <= 7) {
        return `0(${cleanNumbers.slice(1, 4)}) ${cleanNumbers.slice(4)}`;
      } else if (cleanNumbers.length <= 9) {
        return `0(${cleanNumbers.slice(1, 4)}) ${cleanNumbers.slice(4, 7)} ${cleanNumbers.slice(7)}`;
      } else {
        return `0(${cleanNumbers.slice(1, 4)}) ${cleanNumbers.slice(4, 7)} ${cleanNumbers.slice(7, 9)} ${cleanNumbers.slice(9, 11)}`;
      }
    }
  };

  // WhatsApp doğrulama kodu gönder (arka planda - UI'ı bloklamasın)
  const sendVerificationCode = async () => {
    if (!formData.phone) {
      setError('Lütfen telefon numaranızı girin');
      return;
    }

    setError('');

    // Hemen sayaç başlat - kullanıcı beklemesin
    setVerificationCodeSent(true);
    setCodeExpiresAt(new Date(Date.now() + 3 * 60 * 1000));
    setCodeTimer(180); // 3 dakika = 180 saniye

    // Mesajı arka planda gönder - UI'ı bloklamasın
    Promise.resolve().then(async () => {
      try {
        let cleanPhone = formData.phone.replace(/\D/g, '');
        // +90 seçildiyse ve 0 ile başlıyorsa kaldır
        if (countryCode === '+90' && cleanPhone.startsWith('0')) {
          cleanPhone = cleanPhone.slice(1);
        }
        const response = await fetch('/api/send-verification-code', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            phone: cleanPhone, 
            cloudflare_token: cloudflareToken || null // Cloudflare token varsa gönder, yoksa null
          })
        });

        const data = await response.json();

        if (!data.success) {
          // Hata olursa sadece hata mesajını göster, kod girme alanı açık kalsın
          // Kullanıcı tekrar gönderebilir veya kod girebilir
          setError(data.message || 'Kod gönderilemedi');
          // Cloudflare token'ı sıfırla
          if (cloudflareWidgetRef.current?.dataset?.widgetId) {
            window.turnstile?.reset(cloudflareWidgetRef.current.dataset.widgetId);
          }
          setCloudflareToken(null);
          setVerificationCodeSent(false);
          setCodeTimer(0);
          // Sayaç devam etsin, kullanıcı kod girebilir veya tekrar gönderebilir
        }
      } catch (error) {
        // Hata olursa sadece hata mesajını göster, kod girme alanı açık kalsın
        setError('Sunucu hatası. Lütfen tekrar deneyin.');
        // Cloudflare token'ı sıfırla
        if (cloudflareWidgetRef.current?.dataset?.widgetId) {
          window.turnstile?.reset(cloudflareWidgetRef.current.dataset.widgetId);
        }
        setCloudflareToken(null);
        setVerificationCodeSent(false);
        setCodeTimer(0);
        // Sayaç devam etsin, kullanıcı kod girebilir veya tekrar gönderebilir
      }
    });
  };

  // Doğrulama kodunu kontrol et
  const verifyCode = async () => {
    if (!formData.verificationCode || formData.verificationCode.length !== 6) {
      setError('Lütfen 6 haneli kodu girin');
      return;
    }

    setLoading(true);
    setError('');

    try {
      let cleanPhone = formData.phone.replace(/\D/g, '');
      // +90 seçildiyse ve 0 ile başlıyorsa kaldır
      if (countryCode === '+90' && cleanPhone.startsWith('0')) {
        cleanPhone = cleanPhone.slice(1);
      }
      const response = await fetch('/api/verify-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: cleanPhone, code: formData.verificationCode })
      });

      const data = await response.json();

      if (data.success) {
        setCodeVerified(true);
        setError('');
        // Bir sonraki adıma geç
        setTimeout(() => {
          setCurrentStep(3);
        }, 500);
      } else {
        setError(data.message || 'Geçersiz kod');
      }
    } catch (error) {
      setError('Sunucu hatası. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  // Kayıt işlemi
  const handleRegister = async () => {
    if (!formData.fullName || !formData.username || !formData.password || !formData.confirmPassword) {
      setError('Lütfen tüm alanları doldurun');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Şifreler eşleşmiyor');
      return;
    }

    if (usernameValidation.available === false) {
      setError('Lütfen müsait bir kullanıcı adı seçin');
      return;
    }

    setLoading(true);
    setError('');

    try {
      let cleanPhone = formData.phone.replace(/\D/g, '');
      // +90 seçildiyse ve 0 ile başlıyorsa kaldır
      if (countryCode === '+90' && cleanPhone.startsWith('0')) {
        cleanPhone = cleanPhone.slice(1);
      }
      const response = await fetch('/api/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: formData.username,
          fullName: formData.fullName,
          whatsappNumber: cleanPhone,
          email: null,
          password: formData.password
        })
      });

      if (!response.ok) {
        console.error('HTTP hatası:', response.status, response.statusText);
      }

      const data = await response.json();

      if (data.success) {
        // Kayıt başarılı - son adıma geç
        setCurrentStep(5);
      } else {
        setError(data.message || 'Kayıt başarısız');
        console.error('Kayıt hatası:', data);
      }
    } catch (error) {
      console.error('Kayıt hatası:', error);
      setError('Sunucu hatası. Lütfen tekrar deneyin. Hata: ' + (error.message || 'Bilinmeyen hata'));
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { number: 1, label: 'Hesap Tipi' },
    { number: 2, label: 'Telefon' },
    { number: 3, label: 'Hesap' },
    { number: 4, label: 'Sözleşme' }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'Arial, sans-serif',
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '20px',
        padding: '40px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.1)',
        width: '100%',
        maxWidth: '600px'
      }}>
        {/* Progress Bar */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '40px',
          position: 'relative'
        }}>
          {steps.map((step, index) => (
            <React.Fragment key={step.number}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                flex: 1
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: currentStep > step.number ? '#28a745' : currentStep === step.number ? '#007bff' : '#e2e8f0',
                  color: currentStep >= step.number ? 'white' : '#999',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  marginBottom: '8px'
                }}>
                  {currentStep > step.number ? <i className="fas fa-check"></i> : step.number}
                </div>
                <span style={{
                  fontSize: '12px',
                  color: currentStep >= step.number ? '#333' : '#999',
                  fontWeight: currentStep === step.number ? 'bold' : 'normal'
                }}>
                  {step.label}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div style={{
                  flex: 1,
                  height: '2px',
                  background: currentStep > step.number ? '#28a745' : '#e2e8f0',
                  margin: '0 10px',
                  marginTop: '-30px'
                }} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            background: '#fee',
            color: '#c53030',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '14px',
            border: '1px solid #fed7d7'
          }}>
            <i className="fas fa-exclamation-triangle" style={{ marginRight: '8px' }}></i> {error}
          </div>
        )}

        {/* Step 1: Hesap Tipi */}
        {currentStep === 1 && (
          <div>
            <h2 style={{ marginBottom: '10px', color: '#333', fontSize: '24px' }}>
              Hesap Tipi Seçin
            </h2>
            <p style={{ marginBottom: '30px', color: '#666' }}>
              Hangi tür hesap açmak istersiniz?
            </p>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '20px',
              marginBottom: '30px'
            }}>
              <div
                onClick={() => {
                  setFormData({ ...formData, accountType: 'individual' });
                  setTimeout(() => setCurrentStep(2), 300);
                }}
                style={{
                  padding: '30px',
                  border: `2px solid ${formData.accountType === 'individual' ? '#007bff' : '#e2e8f0'}`,
                  borderRadius: '12px',
                  cursor: 'pointer',
                  textAlign: 'center',
                  transition: 'all 0.3s',
                  background: formData.accountType === 'individual' ? '#f0f7ff' : 'white'
                }}
                onMouseEnter={(e) => {
                  if (formData.accountType !== 'individual') {
                    e.currentTarget.style.borderColor = '#007bff';
                  }
                }}
                onMouseLeave={(e) => {
                  if (formData.accountType !== 'individual') {
                    e.currentTarget.style.borderColor = '#e2e8f0';
                  }
                }}
              >
                <div style={{ fontSize: '48px', marginBottom: '15px', color: '#007bff' }}>
                  <i className="fas fa-user"></i>
                </div>
                <div style={{ fontWeight: 'bold', fontSize: '18px', marginBottom: '5px' }}>
                  Bireysel
                </div>
                <div style={{ color: '#666', fontSize: '14px' }}>
                  Kişisel kullanım için
                </div>
              </div>

              <div
                onClick={() => {
                  setFormData({ ...formData, accountType: 'corporate' });
                  setTimeout(() => setCurrentStep(2), 300);
                }}
                style={{
                  padding: '30px',
                  border: `2px solid ${formData.accountType === 'corporate' ? '#007bff' : '#e2e8f0'}`,
                  borderRadius: '12px',
                  cursor: 'pointer',
                  textAlign: 'center',
                  transition: 'all 0.3s',
                  background: formData.accountType === 'corporate' ? '#f0f7ff' : 'white'
                }}
                onMouseEnter={(e) => {
                  if (formData.accountType !== 'corporate') {
                    e.currentTarget.style.borderColor = '#007bff';
                  }
                }}
                onMouseLeave={(e) => {
                  if (formData.accountType !== 'corporate') {
                    e.currentTarget.style.borderColor = '#e2e8f0';
                  }
                }}
              >
                <div style={{ fontSize: '48px', marginBottom: '15px', color: '#007bff' }}>
                  <i className="fas fa-briefcase"></i>
                </div>
                <div style={{ fontWeight: 'bold', fontSize: '18px', marginBottom: '5px' }}>
                  Kurumsal
                </div>
                <div style={{ color: '#666', fontSize: '14px' }}>
                  Şirketler için
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Telefon Doğrulama */}
        {currentStep === 2 && (
          <div>
            <h2 style={{ marginBottom: '10px', color: '#333', fontSize: '24px' }}>
              Telefon Doğrulama
            </h2>
            <p style={{ marginBottom: '30px', color: '#666' }}>
              Telefon numaranızı doğrulayın
            </p>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Telefon Numarası
              </label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <select 
                  value={countryCode}
                  onChange={(e) => {
                    setCountryCode(e.target.value);
                    // Telefon numarasını yeniden formatla
                    if (formData.phone) {
                      const numbers = formData.phone.replace(/\D/g, '');
                      const formatted = formatPhoneNumber(numbers, e.target.value);
                      setFormData({ ...formData, phone: formatted });
                    }
                  }}
                  style={{
                    padding: '12px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '16px'
                  }}
                >
                  <option value="+90">🇹🇷 +90</option>
                </select>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => {
                    const formatted = formatPhoneNumber(e.target.value, countryCode);
                    setFormData({ ...formData, phone: formatted });
                  }}
                  placeholder={countryCode === '+90' ? "530 XXX XX XX" : "0(5XX) XXX XX XX"}
                  maxLength="17"
                  style={{
                    flex: 1,
                    padding: '12px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontFamily: 'monospace'
                  }}
                />
              </div>
            </div>

            {codeVerified && (
              <div style={{
                background: '#d4edda',
                color: '#155724',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <i className="fas fa-check-circle" style={{ color: '#28a745' }}></i>
                <span>Başarılı!</span>
              </div>
            )}

            {!codeVerified && (
              <>
                {!verificationCodeSent ? (
                  <>
                    {/* Cloudflare Turnstile */}
                    <div 
                      ref={cloudflareWidgetRef}
                      style={{ 
                        marginBottom: '20px',
                        display: 'flex',
                        justifyContent: 'center'
                      }}
                    />
                    
                    <button
                      onClick={sendVerificationCode}
                      disabled={!formData.phone}
                      style={{
                        width: '100%',
                        padding: '15px',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        color: 'white',
                        background: !formData.phone ? '#a0aec0' : '#007bff',
                        border: 'none',
                        borderRadius: '10px',
                        cursor: !formData.phone ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px'
                      }}
                    >
                      <i className="fab fa-whatsapp" style={{ fontSize: '20px' }}></i>
                      WhatsApp Kodu Gönder
                    </button>
                  </>
                ) : (
                  <>
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                        Doğrulama Kodu
                      </label>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <input
                          type="text"
                          value={formData.verificationCode}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                            setFormData({ ...formData, verificationCode: value });
                          }}
                          placeholder="6 haneli kod"
                          maxLength="6"
                          style={{
                            flex: 1,
                            padding: '12px',
                            border: '2px solid #e2e8f0',
                            borderRadius: '8px',
                            fontSize: '18px',
                            textAlign: 'center',
                            fontFamily: 'monospace',
                            letterSpacing: '5px'
                          }}
                        />
                        {codeTimer > 0 ? (
                          <button
                            disabled
                            style={{
                              padding: '12px 20px',
                              border: '1px solid #e2e8f0',
                              borderRadius: '8px',
                              background: '#f5f5f5',
                              color: '#999',
                              cursor: 'not-allowed'
                            }}
                          >
                            {Math.floor(codeTimer / 60)}:{(codeTimer % 60).toString().padStart(2, '0')}
                          </button>
                        ) : (
                          <button
                            onClick={sendVerificationCode}
                            disabled={loading}
                            style={{
                              padding: '12px 20px',
                              border: '1px solid #007bff',
                              borderRadius: '8px',
                              background: 'white',
                              color: '#007bff',
                              cursor: loading ? 'not-allowed' : 'pointer'
                            }}
                          >
                            Tekrar Gönder
                          </button>
                        )}
                      </div>
                      <p style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                        Telefonunuza gönderilen 6 haneli kodu giriniz
                      </p>
                    </div>

                    <button
                      onClick={verifyCode}
                      disabled={loading || formData.verificationCode.length !== 6}
                      style={{
                        width: '100%',
                        padding: '15px',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        color: 'white',
                        background: loading || formData.verificationCode.length !== 6 ? '#a0aec0' : '#007bff',
                        border: 'none',
                        borderRadius: '10px',
                        cursor: loading || formData.verificationCode.length !== 6 ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {loading ? 'Doğrulanıyor...' : 'Devam Et'}
                    </button>
                  </>
                )}
              </>
            )}

            <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
              <button
                onClick={() => setCurrentStep(1)}
                style={{
                  flex: 1,
                  padding: '12px',
                  border: '1px solid #007bff',
                  borderRadius: '8px',
                  background: 'white',
                  color: '#007bff',
                  cursor: 'pointer'
                }}
              >
                Geri
              </button>
              {codeVerified && (
                <button
                  onClick={() => setCurrentStep(3)}
                  style={{
                    flex: 1,
                    padding: '12px',
                    border: 'none',
                    borderRadius: '8px',
                    background: '#007bff',
                    color: 'white',
                    cursor: 'pointer'
                  }}
                >
                  Devam Et
                </button>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Hesap Bilgileri */}
        {currentStep === 3 && (
          <div>
            <h2 style={{ marginBottom: '10px', color: '#333', fontSize: '24px' }}>
              Hesap Bilgileri
            </h2>
            <p style={{ marginBottom: '30px', color: '#666' }}>
              Kullanıcı bilgilerinizi girin
            </p>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Adınız Soyadınız yada Firma Adı
              </label>
              <input
                type="text"
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                placeholder="Ad Soyad veya Firma Adı"
                required
                style={{
                  width: '100%',
                  padding: '15px',
                  fontSize: '16px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '10px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Kullanıcı Adı
              </label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                placeholder="Kullanıcı Adı"
                required
                style={{
                  width: '100%',
                  padding: '15px',
                  fontSize: '16px',
                  border: `2px solid ${usernameValidation.available === false ? '#dc3545' : usernameValidation.available === true ? '#28a745' : '#e2e8f0'}`,
                  borderRadius: '10px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
              {usernameValidation.message && (
                <div style={{
                  marginTop: '8px',
                  fontSize: '14px',
                  color: usernameValidation.available === false ? '#dc3545' : usernameValidation.available === true ? '#28a745' : '#666'
                }}>
                  {usernameValidation.checking && <i className="fas fa-spinner fa-spin" style={{ marginRight: '5px' }}></i>}
                  {usernameValidation.message}
                </div>
              )}
              <div style={{ marginTop: '5px', fontSize: '12px', color: '#666' }}>
                En az 3 karakter, sadece harf, rakam ve alt çizgi
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Şifre
              </label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Şifre"
                required
                style={{
                  width: '100%',
                  padding: '15px',
                  fontSize: '16px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '10px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
              <div style={{ marginTop: '5px', fontSize: '12px', color: '#666' }}>
                En az 8 karakter, bir büyük harf ve bir rakam
              </div>
            </div>

            <div style={{ marginBottom: '30px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Şifre Tekrar
              </label>
              <input
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                placeholder="Şifre Tekrar"
                required
                style={{
                  width: '100%',
                  padding: '15px',
                  fontSize: '16px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '10px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => setCurrentStep(2)}
                style={{
                  flex: 1,
                  padding: '12px',
                  border: '1px solid #007bff',
                  borderRadius: '8px',
                  background: 'white',
                  color: '#007bff',
                  cursor: 'pointer'
                }}
              >
                Geri
              </button>
              <button
                onClick={() => setCurrentStep(4)}
                disabled={!formData.fullName || !formData.username || usernameValidation.available !== true || !formData.password || !formData.confirmPassword}
                style={{
                  flex: 1,
                  padding: '12px',
                  border: 'none',
                  borderRadius: '8px',
                  background: (!formData.fullName || !formData.username || usernameValidation.available !== true || !formData.password || !formData.confirmPassword) ? '#a0aec0' : '#007bff',
                  color: 'white',
                  cursor: (!formData.fullName || !formData.username || usernameValidation.available !== true || !formData.password || !formData.confirmPassword) ? 'not-allowed' : 'pointer'
                }}
              >
                Devam Et
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Üyelik Sözleşmesi */}
        {currentStep === 4 && (
          <div>
            <h2 style={{ marginBottom: '10px', color: '#333', fontSize: '24px' }}>
              Hizmet Sözleşmesi ve Gizlilik Politikası
            </h2>
            <p style={{ marginBottom: '30px', color: '#666' }}>
              Son adım! Lütfen sözleşmeleri okuyun
            </p>

            <div style={{
              maxHeight: '250px',
              overflowY: 'auto',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '15px',
              background: '#f9f9f9'
            }}>
              <h3 style={{ marginTop: 0, marginBottom: '15px', fontSize: '16px', fontWeight: 'bold', color: '#333' }}>
                Hizmet Sözleşmesi
              </h3>
              <div style={{ whiteSpace: 'pre-wrap', fontSize: '13px', lineHeight: '1.6', color: '#333' }}>
                {membershipAgreementText || 'Hizmet sözleşmesi yükleniyor...'}
              </div>
            </div>

            <div style={{
              maxHeight: '250px',
              overflowY: 'auto',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '20px',
              background: '#f9f9f9'
            }}>
              <h3 style={{ marginTop: 0, marginBottom: '15px', fontSize: '16px', fontWeight: 'bold', color: '#333' }}>
                Gizlilik Politikası
              </h3>
              <div style={{ whiteSpace: 'pre-wrap', fontSize: '13px', lineHeight: '1.6', color: '#333' }}>
                {privacyPolicyText || 'Gizlilik politikası yükleniyor...'}
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={agreementAccepted}
                  onChange={(e) => setAgreementAccepted(e.target.checked)}
                  style={{ width: '20px', height: '20px' }}
                />
                <span>
                  Hizmet Sözleşmesini ve Gizlilik Politikasını okudum, anladım ve kabul ediyorum.
                </span>
              </label>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => setCurrentStep(3)}
                style={{
                  flex: 1,
                  padding: '12px',
                  border: '1px solid #007bff',
                  borderRadius: '8px',
                  background: 'white',
                  color: '#007bff',
                  cursor: 'pointer'
                }}
              >
                Geri
              </button>
              <button
                onClick={handleRegister}
                disabled={loading || !agreementAccepted}
                style={{
                  flex: 1,
                  padding: '12px',
                  border: 'none',
                  borderRadius: '8px',
                  background: loading || !agreementAccepted ? '#a0aec0' : '#007bff',
                  color: 'white',
                  cursor: loading || !agreementAccepted ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? 'Kaydediliyor...' : 'Kaydı Tamamla'}
              </button>
            </div>
          </div>
        )}

        {/* Step 5: Kayıt Başarılı */}
        {currentStep === 5 && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '80px', marginBottom: '20px', color: '#28a745' }}>
              <i className="fas fa-check-circle"></i>
            </div>
            <h2 style={{ marginBottom: '20px', color: '#28a745', fontSize: '28px', fontWeight: 'bold' }}>
              Kayıt Başarılı!
            </h2>
            <div style={{ marginBottom: '30px', color: '#666', lineHeight: '1.8' }}>
              <p style={{ marginBottom: '15px' }}>
                Hesabınızın onaylanması için en kısa sürede değerlendireceğiz, hesabınız aktif olduktan sonra artık e-ticaret sitenizin vazgeçilmez bir aracına sahip olacaksınız.
              </p>
              <p>
                Hayırlı ve bol kazançlar dileriz.
              </p>
            </div>
            <button
              onClick={() => navigate('/login')}
              style={{
                padding: '15px 40px',
                border: 'none',
                borderRadius: '10px',
                background: '#007bff',
                color: 'white',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              Giriş Sayfasına Dön
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RegisterPage;

